import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit2, Trash2, Loader2, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { BlogPostForm } from './admin';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

// Initial form state
const getInitialFormData = () => ({
  title_en: '',
  title_tr: '',
  excerpt_en: '',
  excerpt_tr: '',
  content_en: '',
  content_tr: '',
  author: '',
  authorRole_en: '',
  authorRole_tr: '',
  category_en: '',
  category_tr: '',
  image: '',
  featured: false,
  status: 'published'
});

// Post list item component
const PostListItem = ({ post, onEdit, onDelete }) => (
  <tr className="hover:bg-gray-50 transition-colors">
    <td className="py-4 px-6">
      <div className="flex items-center gap-4">
        <img 
          src={post.image} 
          alt={post.title_en}
          className="w-16 h-12 object-cover rounded-lg"
        />
        <div>
          <h4 className="font-semibold text-gray-900 line-clamp-1">{post.title_en}</h4>
          <p className="text-sm text-gray-500 line-clamp-1">{post.excerpt_en}</p>
          {post.title_tr && (
            <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded mt-1">
              TR available
            </span>
          )}
        </div>
      </div>
    </td>
    <td className="py-4 px-6">
      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
        {post.category}
      </span>
    </td>
    <td className="py-4 px-6">
      <div>
        <div className="font-medium text-gray-900">{post.author}</div>
        <div className="text-sm text-gray-500">{post.authorRole_en}</div>
      </div>
    </td>
    <td className="py-4 px-6 text-gray-600">
      {new Date(post.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })}
    </td>
    <td className="py-4 px-6">
      <div className="flex flex-col gap-1">
        {post.status === 'draft' ? (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full inline-block w-fit">
            Draft
          </span>
        ) : (
          <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full inline-block w-fit">
            Published
          </span>
        )}
        {post.featured && (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full inline-block w-fit">
            Featured
          </span>
        )}
      </div>
    </td>
    <td className="py-4 px-6">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => onEdit(post)}
          className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
          title="Edit"
          data-testid={`edit-post-${post.id}`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(post.id)}
          className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
          title="Delete"
          data-testid={`delete-post-${post.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </td>
  </tr>
);

// Stats component
const BlogStats = ({ posts }) => (
  <div className="mt-8 grid md:grid-cols-4 gap-6">
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="text-3xl font-bold text-gray-900">{posts.length}</div>
      <div className="text-gray-500">Total Posts</div>
    </div>
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="text-3xl font-bold text-green-600">
        {posts.filter(p => p.status !== 'draft').length}
      </div>
      <div className="text-gray-500">Published</div>
    </div>
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="text-3xl font-bold text-gray-500">
        {posts.filter(p => p.status === 'draft').length}
      </div>
      <div className="text-gray-500">Drafts</div>
    </div>
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="text-3xl font-bold text-yellow-600">
        {posts.filter(p => p.featured).length}
      </div>
      <div className="text-gray-500">Featured</div>
    </div>
  </div>
);

const BlogAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(getInitialFormData());

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl('blog/posts/admin'));
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      logger.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuth) {
      fetchPosts();
    }
  }, [isAuth, fetchPosts]);

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData());
    setEditingPost(null);
    setShowForm(false);
    setActiveTab('en');
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await fetch(apiUrl('upload'), {
        method: 'POST',
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        const imageUrl = `${API_URL}${data.url}`;
        setFormData(prev => ({ ...prev, image: imageUrl }));
        setImagePreview(imageUrl);
        toast.success('Image uploaded successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = useCallback((post) => {
    setFormData({
      title_en: post.title_en || '',
      title_tr: post.title_tr || '',
      excerpt_en: post.excerpt_en || '',
      excerpt_tr: post.excerpt_tr || '',
      content_en: post.content_en || '',
      content_tr: post.content_tr || '',
      author: post.author,
      authorRole_en: post.authorRole_en || '',
      authorRole_tr: post.authorRole_tr || '',
      category_en: post.category_en || '',
      category_tr: post.category_tr || '',
      image: post.image,
      featured: post.featured,
      status: post.status || 'published'
    });
    setImagePreview(post.image);
    setEditingPost(post);
    setShowForm(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.content_en || formData.content_en.trim().length < 50) {
      toast.error('English content must be at least 50 characters');
      return;
    }

    setIsSaving(true);

    try {
      const url = editingPost 
        ? apiUrl(`blog/posts/${editingPost.id}`)
        : apiUrl('blog/posts');
      
      const response = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const statusText = formData.status === 'draft' ? 'saved as draft' : 'published';
        toast.success(`Post ${statusText} successfully!`);
        resetForm();
        fetchPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save post');
      }
    } catch (error) {
      logger.error('Error saving post:', error);
      toast.error('Failed to save post');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(apiUrl(`blog/posts/${postId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Post deleted successfully!');
        fetchPosts();
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      logger.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Blog Admin" />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader
        title="Blog Admin"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        onAddNew={() => { resetForm(); setShowForm(true); }}
        addNewLabel="New Post"
      />

      <div className="container mx-auto px-6 py-8">
        {/* Post Form Modal */}
        {showForm && (
          <BlogPostForm
            formData={formData}
            setFormData={setFormData}
            editingPost={editingPost}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            imagePreview={imagePreview}
            fileInputRef={fileInputRef}
            onImageUpload={handleImageUpload}
            onSubmit={handleSubmit}
            onClose={resetForm}
            isSaving={isSaving}
            isUploading={isUploading}
          />
        )}

        {/* Posts List */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-md">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No blog posts yet</h3>
            <p className="text-gray-500 mb-6">Create your first blog post to get started</p>
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create First Post
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Post</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Category</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Author</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Date</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {posts.map((post) => (
                    <PostListItem 
                      key={post.id} 
                      post={post} 
                      onEdit={handleEdit} 
                      onDelete={handleDelete} 
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stats */}
        {posts.length > 0 && <BlogStats posts={posts} />}
      </div>
    </div>
  );
};

export default BlogAdmin;
